import axios from 'axios'
import { isServerErrorStatus, TECHNICAL_TEAM_ERROR_MESSAGE } from '@constants/userFacingErrors'
import { useAuthStore } from '@store/useAuthStore'
import { getApiBaseUrl } from '@utils/apiBaseUrl'
import { logger } from '@utils/logger'

const API_BASE_URL = getApiBaseUrl()

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Fast local model responds in <0.1s, custom model in <5s, Java timeout is 15s
  // 20s timeout is sufficient and prevents hanging
  timeout: 20000, // 20 segundos
})

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // For FormData (file uploads), remove Content-Type so axios sets multipart/form-data with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Log request (without sensitive data)
    await logger.info('API Request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      hasAuth: !!token,
    })
    
    return config
  },
  async (error) => {
    await logger.error('API Request Error', error, {
      message: 'Failed to send request',
    })
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  async (response) => {
    // Backend returns ApiResponse<T> format
    // Extract data from response if it's wrapped in ApiResponse
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // If backend returns { success, data, message }, we can log the message
      if (response.data.success === false) {
        await logger.warn('API Response Warning', {
          url: response.config.url,
          message: response.data.message,
          status: response.status,
        })
      } else {
        // Log successful response
        await logger.info('API Response Success', {
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          status: response.status,
        })
      }
    } else {
      // Log successful response
      await logger.info('API Response Success', {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        status: response.status,
      })
    }
    return response
  },
  async (error) => {
    const status = error.response?.status
    if (isServerErrorStatus(status) && error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as Record<string, unknown>
      data.message = TECHNICAL_TEAM_ERROR_MESSAGE
      data.success = false
    }

    if (status === 401) {
      // Handle unauthorized - clear any stored auth and redirect to login
      localStorage.removeItem('token')
      useAuthStore.getState().logout()

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    
    // Log error (but not for expired tokens to avoid spam)
    if (error.response?.status !== 401) {
      await logger.error('API Response Error', error, {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
        statusText: error.response?.statusText,
      })
    }
    
    return Promise.reject(error)
  }
)

export default api

